import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import Select from 'react-select';
import * as Bixby from '$/core';
import * as Services from '$/services';
import BixButton from '$/components/global/bixButton';
import HeadingBlock from '$/components/global/heading';
import Loading from '$/components/loading/Loading';
import Modal from '$/components/global/modal';
import { toastError, toastSuccess } from '$/store/toast/actions';
import './index.scss';

interface IPreferredClientOrderProps {
  dispatchToastError: (message: string) => void;
  dispatchToastSuccess: (message: string) => void;
}

type GridClient = Bixby.Models.PreferredDataSiteClient;
type SelectOption = { value: string; label: string };

const OrderCellRenderer = (params: any) => {
  const [inputVal, setInputVal] = useState<string>(String(params.value ?? ''));

  useEffect(() => {
    setInputVal(String(params.value ?? ''));
  }, [params.value]);

  const commit = () => {
    const n = Number.parseInt(inputVal, 10);
    if (!Number.isNaN(n) && n > 0) {
      // Only call handleOrderChange if value actually changed
      if (n !== params.value) {
        params.context.handleOrderChange(params.data.clientId, n);
      }
    } else {
      setInputVal(String(params.value ?? ''));
    }
  };

  return (
    <input
      type="number"
      min={1}
      max={params.context.totalCount}
      value={inputVal}
      className="pco_order_input"
      onChange={e => setInputVal(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') commit();
      }}
    />
  );
};

const RemoveCellRenderer = (params: any) => (
  <button
    className="fa fa-minus-circle pco_remove_btn"
    title="Remove client"
    onClick={() => params.context.handleRemoveClick(params.data)}
  />
);

const AdminPreferredClientOrder: React.FC<IPreferredClientOrderProps> = ({
  dispatchToastError,
  dispatchToastSuccess,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [clients, setClients] = useState<GridClient[]>([]);
  const [availableClients, setAvailableClients] = useState<Bixby.Models.PreferredClientSlim[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedToAdd, setSelectedToAdd] = useState<SelectOption[]>([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [clientToRemove, setClientToRemove] = useState<GridClient | null>(null);

  const gridApiRef = useRef<any>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [all, available] = await Promise.all([
          Services.PreferredDataSiteClientService.getAll(),
          Services.PreferredDataSiteClientService.getAvailableClients(),
        ]);
        setClients([...all].sort((a, b) => a.priorityOrder - b.priorityOrder));
        setAvailableClients(available);
      } catch {
        dispatchToastError('Failed to load preferred client order.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [dispatchToastError]);

  const reorderClients = useCallback((arr: GridClient[], movedClientId: string, targetOrder: number): GridClient[] => {
    const sorted = [...arr].sort((a, b) => a.priorityOrder - b.priorityOrder);
    const fromIdx = sorted.findIndex(c => c.clientId === movedClientId);
    if (fromIdx === -1) return arr;
    const clamped = Math.max(1, Math.min(targetOrder, sorted.length));
    const [moved] = sorted.splice(fromIdx, 1);
    sorted.splice(clamped - 1, 0, moved);
    return sorted.map((c, i) => ({ ...c, priorityOrder: i + 1 }));
  }, []);

  const handleOrderChange = useCallback(
    (clientId: string, newOrder: number) => {
      setClients(prev => {
        const reordered = reorderClients(prev, clientId, newOrder);
        // Only update if order actually changed
        const hasChanged = reordered.some(
          (c, i) => c.clientId !== prev[i]?.clientId || c.priorityOrder !== prev[i]?.priorityOrder
        );
        if (hasChanged) {
          setIsDirty(true);
          return reordered;
        }
        return prev;
      });
    },
    [reorderClients]
  );

  const handleRemoveClick = useCallback((client: GridClient) => {
    setClientToRemove(client);
    setShowRemoveModal(true);
  }, []);

  const confirmRemove = () => {
    if (!clientToRemove) return;
    setClients(prev => {
      const filtered = prev.filter(c => c.clientId !== clientToRemove.clientId);
      return filtered.map((c, i) => ({ ...c, priorityOrder: i + 1 }));
    });
    setAvailableClients(prev => [...prev, { id: clientToRemove.clientId, name: clientToRemove.clientName }]);
    setClientToRemove(null);
    setShowRemoveModal(false);
    setIsDirty(true);
  };

  const closeRemoveModal = () => {
    setShowRemoveModal(false);
    setClientToRemove(null);
  };

  const handleAdd = () => {
    const nextStart = clients.length + 1;
    const newClients: GridClient[] = selectedToAdd.map((opt, i) => ({
      id: '',
      clientId: opt.value,
      clientName: opt.label,
      priorityOrder: nextStart + i,
    }));
    setClients(prev => [...prev, ...newClients]);
    const addedIds = new Set(selectedToAdd.map(o => o.value));
    setAvailableClients(prev => prev.filter(c => !addedIds.has(c.id)));
    setSelectedToAdd([]);
    setShowAddModal(false);
    setIsDirty(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setSelectedToAdd([]);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await Services.PreferredDataSiteClientService.save(
        clients.map(c => ({ clientId: c.clientId, priorityOrder: c.priorityOrder }))
      );
      dispatchToastSuccess('Preferred client order saved successfully.');
      setIsDirty(false);
    } catch {
      dispatchToastError('Failed to save preferred client order.');
    } finally {
      setIsSaving(false);
    }
  };

  const onRowDragEnd = useCallback(() => {
    const reordered: GridClient[] = [];
    gridApiRef.current?.forEachNode((node: any) => {
      if (node.data) reordered.push(node.data);
    });
    if (reordered.length > 0) {
      setClients(prev => {
        // Check if drag actually changed the order
        const hasChanged = reordered.some((c, i) => c.clientId !== prev[i]?.clientId);
        if (hasChanged) {
          setIsDirty(true);
          return reordered.map((c, i) => ({ ...c, priorityOrder: i + 1 }));
        }
        return prev;
      });
    }
  }, []);

  const gridContext = useMemo(
    () => ({ handleOrderChange, handleRemoveClick, totalCount: clients.length }),
    [handleOrderChange, handleRemoveClick, clients.length]
  );

  const defaultColDef = useMemo(() => ({ resizable: false, lockPinned: true }), []);

  const availableOptions: SelectOption[] = availableClients.map(c => ({ value: c.id, label: c.name }));

  if (isLoading) return <Loading pageLevel />;

  return (
    <div className="pco_container">
      <div className="pco_header">
        <HeadingBlock title="Preferred Client Order" />
        <div className="pco_header_actions">
          <BixButton light onClick={() => navigate('/admin/workflow-management')}>
            ← Back to Workflow Management
          </BixButton>
          <BixButton onClick={() => setShowAddModal(true)}>+ Add Clients</BixButton>
          <BixButton
            className="pco_save_button"
            isLoading={isSaving}
            disabled={!isDirty || isSaving}
            onClick={handleSave}>
            Save
          </BixButton>
        </div>
      </div>

      <div className="pco_body">
        <div className="pco_toolbar">
          <span className="pco_toolbar_title">
            {clients.length} Preferred {clients.length === 1 ? 'Client' : 'Clients'}
          </span>
        </div>
        <div className="pco_grid">
          <div className="grid_container">
            <div className="grid ag-theme-alpine">
              <AgGridReact
                rowData={clients}
                columnDefs={[
                  {
                    headerName: '',
                    width: 40,
                    rowDrag: true,
                    suppressMovable: true,
                    resizable: false,
                    sortable: false,
                    filter: false,
                    suppressMenu: true,
                  },
                  {
                    field: 'priorityOrder',
                    headerName: 'Order',
                    width: 90,
                    sortable: false,
                    filter: false,
                    suppressMenu: true,
                    cellRenderer: OrderCellRenderer,
                  },
                  {
                    field: 'clientName',
                    headerName: 'Client Name',
                    flex: 1,
                    minWidth: 200,
                    sortable: false,
                    filter: false,
                    suppressMenu: true,
                  },
                  {
                    headerName: '',
                    width: 60,
                    sortable: false,
                    filter: false,
                    suppressMenu: true,
                    cellRenderer: RemoveCellRenderer,
                    cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
                  },
                ]}
                defaultColDef={defaultColDef}
                context={gridContext}
                rowDragManaged
                animateRows
                rowHeight={40}
                headerHeight={36}
                suppressMovableColumns
                suppressDragLeaveHidesColumns
                onGridReady={p => {
                  gridApiRef.current = p.api;
                }}
                onRowDragEnd={onRowDragEnd}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal title="Add Preferred Clients" active={showAddModal} onClose={closeAddModal}>
        <p className="pco_modal_label">Select one or more clients to add to the preferred list</p>
        <div className="pco_modal_select_wrapper">
          <Select
            isMulti
            options={availableOptions}
            value={selectedToAdd}
            onChange={opts => setSelectedToAdd((opts as SelectOption[]) ?? [])}
            placeholder="Search clients..."
            classNamePrefix="pco-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
        <div className="pco_modal_actions">
          <BixButton onClick={closeAddModal}>Cancel</BixButton>
          <BixButton className="pco_save_button" disabled={selectedToAdd.length === 0} onClick={handleAdd}>
            Add{selectedToAdd.length > 0 ? ` (${selectedToAdd.length})` : ''}
          </BixButton>
        </div>
      </Modal>

      <Modal title="Remove Client" active={showRemoveModal} onClose={closeRemoveModal}>
        <p className="pco_confirm_text">
          Are you sure you want to remove <strong>{clientToRemove?.clientName}</strong> from the preferred client list?
        </p>
        <div className="pco_confirm_actions">
          <BixButton onClick={confirmRemove}>Remove</BixButton>
          <BixButton light onClick={closeRemoveModal}>
            Cancel
          </BixButton>
        </div>
      </Modal>
    </div>
  );
};

const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastError: (message: string) => dispatch(toastError(message)),
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminPreferredClientOrder);
